import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';
import {
    withStyles, Typography, ButtonBase, useTheme, useMediaQuery,
} from '@material-ui/core';

import {
    getWeekStartDate, datestamp, getWeekNumber,
} from '../../../youwell-common/dateUtils';
import { toLookup, toBoolLookup } from '../../../youwell-common/arrayUtils';

import { TaskName, VimoModal } from '../../../common/components';
import TaskInfo from './TaskInfo';

const itemHeight = 50;

const styles = theme => ({
    root: {
        width: '100%',
        borderBottom: theme.borders.green,

        [theme.breakpoints.down('xs')]: {
            border: 0,
        },
    },
    calendar: {
        width: '100%',
        display: 'flex',
        position: 'relative',

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    day: {
        flex: '1 1 calc(20% - 1px)',
        display: 'flex',
        flexDirection: 'column',

        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(2),
        },
    },
    dayHeader: {
        height: itemHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        background: theme.colors.blue4,
        borderRight: theme.borders.normal,

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'row-reverse',
            justifyContent: 'space-evenly',
            height: 'auto',
            padding: theme.spacing(1),
            border: 0,
        },
    },
    firstDayHeader: {
        borderLeft: theme.borders.normal,
        [theme.breakpoints.down('xs')]: {
            border: 0,
        },
    },
    dayTasks: {
        flex: '1 1 auto',
        borderRight: theme.borders.normal,
        [theme.breakpoints.down('xs')]: {
            border: 0,
        },
        minHeight: itemHeight * 2,
    },
    firstDay: {
        borderLeft: theme.borders.normal,
        [theme.breakpoints.down('xs')]: {
            border: 0,
        },
    },
    availableDay: {
        background: theme.colors.grey1,
    },
    dayLetter: {
        '&:first-letter': {
            textTransform: 'uppercase',
        },
    },
    task: {
        margin: theme.spacing(2),
    },
    noTasksThisWeek: {
        position: 'absolute',
        top: '40%',
        width: `calc(100% - ${theme.spacing(1)}px)`,
        opacity: 0.5,
    },
    skillName: {
        margin: theme.spacing(2, 1),
    },
});

const WeekOverview = ({
    classes, translate, skill, studentData, showDayHeadings,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const [showModal, setShowModal] = React.useState(null);
    const tasks = skill?.moduleSchedule?.tasks.reduce((prev, curr) => (
        {
            ...prev,
            [new Date(curr.date).toISOString()]: [...prev[new Date(curr.date).toISOString()] || [], curr],
        }
    ), {}) || {};

    const taskNames = toLookup(skill?.module?.tasks) || { [skill.taskId]: skill.task };
    const availableDays = toBoolLookup(studentData?.extendedProperties?.availableWeekdays?.split(','));
    const weekNumber = getWeekNumber(new Date());
    const startDate = getWeekStartDate(weekNumber);

    let hasTasksThisWeek = false;
    const weekArray = Array.from(Array(5).keys(), (n) => {
        const d = new Date(startDate.getTime());
        d.setDate(d.getDate() + n);
        d.setHours(d.getHours() + 1); // Adding one hour to compensate for the 01:00 start time of tasks in local time
        hasTasksThisWeek = hasTasksThisWeek || !!tasks[d.toISOString()];
        return {
            date: d,
            datestamp: d.toISOString(),
            tasks: tasks[d.toISOString()],
        };
    });

    return (
        <div className={classes.root}>
            <div className={classes.calendar}>
                {!hasTasksThisWeek ? (
                    <Typography variant="h6" align="center" className={classes.noTasksThisWeek}>
                        {translate('student.noTasksThisWeek')}
                    </Typography>
                ) : (
                    <>
                        <div className={classes.day}>
                            {(showDayHeadings || isMobile) && (
                                <div className={classnames(classes.dayHeader, classes.firstDayHeader)}>
                                    <Typography variant="subtitle1" color="inherit" className={classes.dayLetter}>
                                        {translate('student.skill')}
                                    </Typography>
                                </div>
                            )}
                            <Typography className={classes.skillName}>
                                {skill?.module?.name}
                            </Typography>
                        </div>
                        {weekArray.map((day, i) => (
                            <div key={day.date} className={classes.day}>
                                {(showDayHeadings || isMobile) && (
                                    <div className={classnames(classes.dayHeader)}>
                                        <Typography variant="caption" color="inherit">
                                            {datestamp(day.date).slice(0, 5)}
                                        </Typography>
                                        <Typography variant="subtitle1" color="inherit" className={classes.dayLetter}>
                                            {translate(`time.weekdays.${day.date.getDay()}`)}
                                        </Typography>
                                    </div>
                                )}
                                <div
                                    className={classnames(classes.dayTasks, { [classes.availableDay]: availableDays[i.toString()] })}
                                >
                                    {day.tasks && day.tasks.map(taskDay => (
                                        <ButtonBase key={taskDay.taskId} onClick={() => setShowModal(taskDay.taskId)}>
                                            <TaskName task={taskNames[taskDay.taskId]} className={classes.task} />
                                        </ButtonBase>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            <VimoModal
                open={!!showModal}
                onClose={() => setShowModal(null)}
                heading={(<TaskName task={taskNames[showModal]} />)}
            >
                <TaskInfo taskId={showModal} />
            </VimoModal>
        </div>
    );
};

WeekOverview.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    skill: PropTypes.object,
    studentData: PropTypes.object.isRequired,
    showDayHeadings: PropTypes.bool,
};

WeekOverview.defaultProps = {
    skill: null,
    showDayHeadings: false,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
});

export default connect(mapStateToProps)(withStyles(styles)(WeekOverview));
